import { zodResolver } from "@hookform/resolvers/zod";
import Axios, { AxiosError } from "axios";
import {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { ColumnDef } from "@tanstack/react-table";
import { endpoints } from "../../../../endpoints";
import { Form } from "../../../../layout/FormLayout";
import type {
  LeadAssignee,
  OptionType,
  User,
  UUID,
} from "../../../../types/types";
import { strings } from "../../../../util/strings";
import {
  convertUserToOption,
  useFormWrapper,
  useStoreState,
} from "../../../../util/util";
import {
  DeleteButton,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import { SectionTitle } from "../../../../components/Form/Form";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { SearchSelectInfiniteScroll } from "../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { SelectBoxV2 } from "../../../../components/SelectBoxV2/SelectBoxV2";
import { Table } from "../../../../components/Table/Table";
import { useRegionsList, useStatesList } from "../../../../util/Locations";
import { rowHover } from "../../../../util/util";
import { getRegionsLocation, Region, RegionMultiSelectFieldRef } from "./util";
import { RegionMultiSelectField } from "./RegionMultiSelectField";
import styled from "styled-components/macro";
import { ActionMeta } from "react-select";
import { spacingTokens } from "../../../../designTokens";

const MultiValue = () => null;

// Button group container matching Figma design specifications
const ButtonGroupFullWidthFlex = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  align-items: stretch;
  width: 100%;

  > * {
    flex: 1;
    min-width: 0;
  }
`;

// Note: user emails cannot be edited using this form, but we do want to
// show the email address because currently there's no other way to view it.
interface FormInputs {
  countries: OptionType<string>[];
  states: OptionType<string>[];
  custom_region: OptionType<string>;
  lead_assignee: OptionType<LeadAssignee>;
  lead_subscribers: OptionType<LeadAssignee>[];
}

type AddOrEditRegionProps = {
  region?: Region;
  onSuccess: () => void;
  onError: (error: unknown) => void;
  onCancel: () => void;
  countries: OptionType<string>[];
};

export function AddOrEditRegion({
  region,
  onSuccess,
  onError,
  onCancel,
  countries,
}: AddOrEditRegionProps) {
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { storefront_id, tenant_id } = useStoreState();
  const [submitting, setSubmitting] = useState(false);
  const [tableData, setTableData] = useState(() =>
    (region?.lead_subscribers || []).map((user) => ({
      ...user,
      label: convertUserToOption(user).label,
    }))
  );
  const [selectedUsers, setSelectedUsers] = useState(() =>
    (region?.lead_subscribers || []).map((user) => ({
      label: convertUserToOption(user).label,
      value: user,
    }))
  );
  const { t } = useTranslation();
  const statesMultiSelectFieldRef = useRef<RegionMultiSelectFieldRef>(null);

  const zodSchema = z
    .object({
      countries: z
        .array(
          z.object({
            value: z.string(),
            label: z.string(),
          })
        )
        .min(1, strings(t).thisIsARequiredField),
      states: z
        .array(
          z.object({
            value: z.string(),
            label: z.string(),
          })
        )
        .optional()
        .default([]),
      custom_region: z.object({
        value: z.string().min(1, strings(t).thisIsARequiredField),
        label: z.string().min(1, strings(t).thisIsARequiredField),
      }),
      lead_assignee: z.object({
        value: z.any(),
        label: z.string().min(1, strings(t).thisIsARequiredField),
      }),
      lead_subscribers: z
        .array(
          z.object({
            value: z.any(),
            label: z.string(),
          })
        )
        .optional()
        .default([]),
    })
    .superRefine(({ countries, states }, ctx) => {
      if (countries.length === 1 && !states.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: strings(t).thisIsARequiredField,
          path: ["states"],
        });
      }
    });

  const get_default_countries = useCallback((region?: Region) => {
    if (!region) return [];
    const countries = getRegionsLocation(region.locations, "country").map(
      ({ alpha_2, country }) => ({
        label: country,
        value: alpha_2,
      })
    );
    return region.is_global ? countries : countries.slice(0, 1);
  }, []);

  const get_default_states = useCallback((region?: Region) => {
    if (!region) return [];
    if (region.is_global) return [];
    return getRegionsLocation(region.locations, "state").map(
      ({ state, subdivision_code }) => ({
        label: state,
        value: subdivision_code,
      })
    );
  }, []);

  const methodsOfUseForm = useFormWrapper<FormInputs>({
    resolver: zodResolver(zodSchema) as any,
    defaultValues: {
      countries: get_default_countries(region),
      states: get_default_states(region), // Will be set after form initialization
      custom_region: region?.custom_region
        ? { label: region.custom_region, value: region.custom_region }
        : { label: "", value: "" },
      lead_assignee: region?.lead_assignee
        ? {
            label: convertUserToOption(region.lead_assignee).label,
            value: region.lead_assignee,
          }
        : { label: "", value: undefined },
      lead_subscribers: (region?.lead_subscribers || []).map((user) => ({
        label: convertUserToOption(user).label,
        value: user,
      })),
    },
  });

  const { handleSubmit, formState, control, errors, setValue, watch, trigger } =
    methodsOfUseForm;

  const regions = useRegionsList();

  // Watch the selected country to get states dynamically
  const maybeCountry = watch("countries");
  const selectedCountry =
    maybeCountry?.length === 1 ? maybeCountry[0].value : null;
  const states = useStatesList(selectedCountry);

  // Watch form values to get current selections
  const leadAssignee = watch("lead_assignee");
  const watchedLeadSubscribers = watch("lead_subscribers");
  const leadSubscribers = useMemo(
    () => watchedLeadSubscribers || [],
    [watchedLeadSubscribers]
  );

  // Memoized filtering logic to prevent duplicate user selections
  const getFilteredPrimaryUserOptions = useMemo(() => {
    return (response: User[]) => {
      const selectedSubscriberIds = leadSubscribers.map(
        (user) => user.value.id
      );
      return response
        .filter(
          (user) => user.is_active && !selectedSubscriberIds.includes(user.id)
        )
        .map((user) => ({
          label: convertUserToOption(user).label,
          value: user,
        }));
    };
  }, [leadSubscribers]);

  const getFilteredAdditionalUserOptions = useMemo(() => {
    return (response: User[]) => {
      const selectedSubscriberIds = [
        leadAssignee?.value?.id,
        ...leadSubscribers.map((user) => user.value.id),
      ];
      return response
        .filter(
          (user) => user.is_active && !selectedSubscriberIds.includes(user.id)
        )
        .map((user) => ({
          label: convertUserToOption(user).label,
          value: user,
        }));
    };
  }, [leadAssignee?.value?.id, leadSubscribers]);

  // Handle country changes - clear state when country changes
  useEffect(() => {
    if (!maybeCountry || maybeCountry.length === 0) {
      setValue("states", []);
      statesMultiSelectFieldRef.current?.clearChips();
    } else if (maybeCountry.length > 1) {
      trigger("states");
    }
  }, [maybeCountry, setValue, trigger]);

  const onSubmit = async (inputs: FormInputs) => {
    try {
      setSubmitting(true);
      const locations: { alpha_2?: string; subdivision_code?: string }[] =
        inputs.countries.length === 1
          ? [
              {
                alpha_2: inputs.countries[0].value,
                subdivision_code: inputs.states[0].value,
              },
              ...inputs.states.slice(1).map(({ value: subdivision_code }) => ({
                alpha_2: inputs.countries[0].value,
                subdivision_code,
              })),
            ]
          : [
              ...inputs.countries.map(({ value: alpha_2 }) => ({ alpha_2 })),
              ...inputs.states.map(({ value: subdivision_code }) => ({
                subdivision_code,
              })),
            ];
      const requestBody = {
        locations,
        custom_region: inputs.custom_region.value,
        lead_assignee_id: inputs.lead_assignee.value.id,
        lead_subscriber_ids: inputs.lead_subscribers.map(
          (user) => user.value.id
        ),
      };

      if (region) {
        // Edit existing region
        await Axios.patch(
          endpoints.v2_storefronts_id_custom_regions_id(
            storefront_id,
            region.id
          ),
          requestBody
        );
        notifySuccess(t("The region has been updated successfully"));
      } else {
        // Create new region
        await Axios.post(
          endpoints.v2_storefronts_id_custom_regions(storefront_id),
          requestBody
        );
        notifySuccess(t("The region has been created successfully"));
      }

      onSuccess();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error creating or editing the region"),
        { error }
      );
      onError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveUser = useCallback(
    (userId: UUID) => {
      const updatedUsers = selectedUsers.filter(
        (user) => user.value.id !== userId
      );
      setSelectedUsers(updatedUsers);
      setValue("lead_subscribers", updatedUsers);
    },
    [selectedUsers, setValue]
  );

  const columns = useMemo<ColumnDef<LeadAssignee & { label: string }>[]>(
    () => [
      {
        id: "userName",
        header: t("Username"),
        accessorFn: (row) => row.label.split(" - ")[0],
      },
      {
        id: "emailId",
        header: t("Email ID"),
        accessorFn: (row) => row?.email_address || row.label.split(" - ")[1],
      },
      {
        id: "actions",
        header: t("Actions"),
        cell: ({ row }) => (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <DeleteButton
              onClick={() => handleRemoveUser(row.original.id)}
              type="button"
              testid="delete"
            />
          </div>
        ),
      },
    ],
    [handleRemoveUser, t]
  );

  useEffect(() => {
    setTableData(
      selectedUsers.map((user) => ({ ...user.value, label: user.label }))
    );
  }, [selectedUsers]);

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={SelectBoxV2}
          control={control}
          name="custom_region"
          autoComplete="custom_region"
          placeholder={t("Region")}
          options={regions}
          rules={{
            required: true,
          }}
          errors={errors}
          formState={formState}
        />
        <RegionMultiSelectField
          name="countries"
          options={countries}
          placeholder={t("Countries")}
          required={true}
          methodsOfUseForm={methodsOfUseForm}
          defaultValues={get_default_countries(region)}
        />
        <RegionMultiSelectField
          name="states"
          options={selectedCountry ? states : []}
          placeholder={t("States")}
          required={true}
          disabled={maybeCountry?.length > 1}
          methodsOfUseForm={methodsOfUseForm}
          ref={statesMultiSelectFieldRef}
          defaultValues={get_default_states(region)}
        />
        <Controller
          name="lead_assignee"
          control={control}
          render={(field) => {
            return (
              <SearchSelectInfiniteScroll
                name={field.name}
                label={t("Primary User")}
                placeholder={t("Primary User")}
                value={field.value}
                onChange={(option: OptionType<User>) => {
                  field.onChange(option);
                }}
                baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                  storefront_id,
                  tenant_id
                )}
                getOptions={getFilteredPrimaryUserOptions}
                params={new URLSearchParams({ is_active: "true" })}
                filterOption={() => true}
                formState={formState}
                errors={errors}
                onBlur={field.onBlur}
              />
            );
          }}
        />
        <SectionTitle style={{ marginTop: "30px" }}>
          {t("Additional Users")}
        </SectionTitle>
        <Controller
          name="lead_subscribers"
          control={control}
          render={(field) => {
            return (
              <SearchSelectInfiniteScroll
                name={field.name}
                label={t("Additional Users")}
                placeholder={t("Additional Users")}
                isMulti
                value={field.value}
                onChange={(
                  options: OptionType<LeadAssignee>[] | null,
                  actionMeta: ActionMeta<LeadAssignee>
                ) => {
                  const selected = options || [];
                  if (actionMeta.action !== "pop-value") {
                    setSelectedUsers(selected);
                    field.onChange(selected);
                  }
                }}
                baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                  storefront_id,
                  tenant_id
                )}
                getOptions={getFilteredAdditionalUserOptions}
                params={
                  new URLSearchParams({ is_active: "true", order_by: "asc" })
                }
                filterOption={() => true}
                formState={formState}
                errors={errors}
                components={{ MultiValue }}
                onBlur={field.onBlur}
              />
            );
          }}
        />
        {tableData.length > 0 && (
          <div style={{ marginTop: spacingTokens.spacing_2xl }}>
            <Table
              columns={columns}
              data={tableData}
              isLoading={false}
              error={undefined}
              rowHover={rowHover}
            />
          </div>
        )}
        <ButtonGroupFullWidthFlex style={{ marginTop: "30px" }}>
          <TertiaryButtonLargeV2 fullWidth onClick={onCancel} type="button">
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 fullWidth type="submit" loading={submitting}>
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonGroupFullWidthFlex>
      </Form>
    </>
  );
}
